import { defaultStoreUtils } from '@databases/redux';
import type { JiraIssueRef } from '@databases/types/confluence';
import { type DatabaseFieldType } from '@databases/types/database';
import { getReferencedJiraIssueRefs } from '@databases/utils/database/field-types/jiraIssueDetails';
import { getDatabaseEntryRequired } from '@databases/utils/database/utils';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { loadJiraIssue } from '@databases/utils/redux/jira/actions';
import { selectJiraIssueOptional } from '@databases/utils/redux/jira/selectors';
import { useCallback, useEffect, useRef, useState } from 'react';

import { type EditComponentProps } from '../types.ts';

// Unified selector for Edit view
export const useJiraEditData = (
  contentId: string,
  entryId: string | undefined,
  field: EditComponentProps<DatabaseFieldType.JIRA_ISSUE_DETAILS>['field']
) => {
  return defaultStoreUtils.useShallowEqualSelector((state) => {
    const database = selectDatabaseByContentIdRequired(state, { contentId });
    const entry = entryId ? getDatabaseEntryRequired(database, entryId) : undefined;
    const refs = entry
      ? getReferencedJiraIssueRefs({ database, field, entry }, state)
      : [];
    const ref = refs[0];
    const issueRef = ref
      ? ({ key: ref.key, cloudId: ref.cloudId, serverId: ref.serverId } as JiraIssueRef)
      : undefined;

    const resolvedIssue = issueRef ? selectJiraIssueOptional(state, issueRef) : undefined;

    const computedSummaryText = (() => {
      const byColumn = resolvedIssue?.columns?.summary?.text;
      return byColumn ?? resolvedIssue?.summary ?? '';
    })();

    return {
      issueRef,
      resolvedIssue,
      computedSummaryText
    };
  });
};

export const useJiraIssueData = (
  contentId: string,
  entryId: string | undefined,
  field: EditComponentProps<DatabaseFieldType.JIRA_ISSUE_DETAILS>['field']
) => {
  const dispatch = defaultStoreUtils.useDispatch();
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    abortControllerRef.current = new AbortController();
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  // Use the unified selector
  const { issueRef, resolvedIssue, computedSummaryText } = useJiraEditData(
    contentId,
    entryId,
    field
  );

  // Ensure summary is loaded
  useEffect(() => {
    if (issueRef && !computedSummaryText && !abortControllerRef.current?.signal.aborted) {
      void dispatch(
        loadJiraIssue(
          { key: issueRef.key, cloudId: issueRef.cloudId, serverId: issueRef.serverId },
          { columns: ['summary'] }
        )
      );
    }
  }, [computedSummaryText, dispatch, issueRef]);

  return { issueRef, resolvedIssue, computedSummaryText, abortControllerRef };
};

export const useEditingState = () => {
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (isEditing) {
      const raf = requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          const len = inputRef.current.value.length;
          if (typeof inputRef.current.setSelectionRange === 'function') {
            inputRef.current.setSelectionRange(len, len);
          }
        }
      });
      return () => cancelAnimationFrame(raf);
    }
  }, [isEditing]);

  return { isEditing, setIsEditing, inputRef };
};

export const useInputValue = (computedSummaryText: string) => {
  const [value, setValue] = useState<string>(computedSummaryText);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    abortControllerRef.current = new AbortController();
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (!abortControllerRef.current?.signal.aborted) {
      setValue(computedSummaryText);
    }
  }, [computedSummaryText]);

  const safeSetValue = useCallback((newValue: string) => {
    if (!abortControllerRef.current?.signal.aborted) {
      setValue(newValue);
    }
  }, []);

  return { value, setValue: safeSetValue };
};
